import * as React from 'react';
import { DetailedErrorComponent, DetailedErrorComponentProps } from './DetailedErrorComponent';
import { DetailedErrorPresModel } from '../gen-src/preslayer/PresModels';
import { Messages } from '../gen-src/Localize';
import { PresentationLayer } from '@tableau/preslayer-api-contract';
import * as Commands from '../gen-src/preslayer/Commands';
import { CommandSerializer, Command } from '@tableau/command-support';

export interface DetailedErrorViewModelProps {
  pm: DetailedErrorPresModel;
  presLayer: PresentationLayer;
  onClose: () => void;
  updatePresModel: (updateAction: (pm: DetailedErrorPresModel) => void) => void;
}

export class DetailedErrorViewModel extends React.Component<DetailedErrorViewModelProps, {}> {

  public constructor(props: DetailedErrorViewModelProps) {
    super(props);
  }

  public errorDialogTitle(): string {
    return this.props.pm.errorDialogTitle || Messages.UnableToCompleteActionText();
  }

  public errorShortMessage(): string {
    return this.props.pm.errorShortMessage || '';
  }

  public errorDetails(): string[] {
    return this.props.pm.errorDetails || [''];
  }

  public hasErrorCode(): boolean {
    return this.props.pm.errorCodeId !== 0;
  }

  public getErrorCodeText(): string {
    return Messages.ErrorCodePrefixText() + this.getErrorCode() + '\n';
  }

  public getErrorCode(): string {
    return this.formatErrorCodePart(this.props.pm.errorCodeId);
  }

  public appendErrorCodeToUrl(url: string): string {
    if (this.hasErrorCode()) {
      let hasQuery = url.indexOf('?') + 1;
      let hasHash = url.indexOf('#') + 1;
      let appendix = (hasQuery ? '&' : '?') + 'errorcode=' + this.getErrorCode();
      url = hasHash ? url.replace('#', appendix + '#') : url + appendix;
    }
    return url;
  }

  public appendErrorCodeToCopyTextArea(): string {
    return this.hasErrorCode() ? this.getErrorCodeText() : '';
  }

  private formatErrorCodePart(part: number): string {
    return part === 0 ? '0' :
      ('00000000' + part.toString(16)).substr(-8).toUpperCase();
  }

  private errorHelpLink(): string {
    let url = this.props.pm.errorHelpLink || 'https://www.tableau.com/support';
    url = this.appendErrorCodeToUrl(url);
    return url;
  }

  private getQueryDetails(): string {
    return this.props.pm.queryDetails || '';
  }

  public issueHelperLinks(): {[key: string]: string } {
    if (this.props.pm.issueHelperLinks) {
      return JSON.parse(this.props.pm.issueHelperLinks);
    }
    return {};
  }

  private getErrorTextToCopy(): string {
    let formattedQueryDetails = this.getQueryDetails() === '' ? '' : '\n' + this.getQueryDetails();

    return this.errorDialogTitle() + '\n' + this.errorShortMessage() + '\n' + this.appendErrorCodeToCopyTextArea()
      + this.errorDetails().join('\n')
      + formattedQueryDetails;
  }

  private callCommand(command: Command | null): Promise<{}> {
    if (command !== null) {
      const params = command.commandParams ? command.commandParams : {};
      return this.props.presLayer.invokeCommand(
        command.commandNamespace, command.commandName, params
      );
    }

    return Promise.reject('command was null');
  }

  private copyText = (text: string): void => {
    if (this.props.pm.useCopyCommand) {
      // tslint:disable-next-line:no-floating-promises
      Commands.CopyTextToClipboard({ valueString: text }, this.props.presLayer);
    } else {
      this.copyTextToClipboard(text);
    }
  }

  private copyTextToClipboard(text: string): void {
    // Place the text in the hidden copy text area
    let textArea = document.getElementById('copy_text_area') as HTMLTextAreaElement;
    textArea.value = text;

    let originalSelectionRange: Range | null = null;
    // Get a selection object representing the range of text selected by the user
    let documentSelection = document.getSelection();
    if (documentSelection) {
      // Set the originalSelection range to start at postion 0
      if (documentSelection.rangeCount > 0) {
        originalSelectionRange = documentSelection.getRangeAt(0);
      }
      // Select the text area
      if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
        this.iosSelectTextArea(textArea);
      } else {
        textArea.select();
      }
      // Copy the selected text
      document.execCommand('copy');
      // Cancel the current selection since we copied to the clipboard
      documentSelection.removeAllRanges();
      // Adds the range to the selection
      if (originalSelectionRange !== null) {
        documentSelection.addRange(originalSelectionRange);
      }
    }
  }

  // This method will make the content editable and create a selection range that can then be copied to the clipboard.
  private iosSelectTextArea(textarea: HTMLTextAreaElement): void {
    let editable = textarea.contentEditable;
    textarea.contentEditable = 'true';
    let range = document.createRange();
    // Sets the range to contain the text
    range.selectNodeContents(textarea);
    let selection = window.getSelection();
    if (selection) {
      // Remove previous selections
      selection.removeAllRanges();
      // Add new selection range
      selection.addRange(range);
    }
    let copiedTextLength = textarea.value.length;
    textarea.setSelectionRange(0, copiedTextLength);
    textarea.contentEditable = editable;
  }


  public render(): JSX.Element {
    let componentProps: DetailedErrorComponentProps = {
      errorDialogTitle: this.errorDialogTitle(),
      errorShortMessage: this.errorShortMessage(),
      errorDetails: this.errorDetails(),
      errorCode: this.hasErrorCode() ? this.getErrorCodeText() : '',
      errorHelpLink: this.errorHelpLink(),
      queryDetails: this.getQueryDetails(),
      issueHelperLinks: this.issueHelperLinks(),
      copyTextValue: this.getErrorTextToCopy(),
      onClose: this.props.onClose,
      copyText: this.copyText,
    };
    return <DetailedErrorComponent {...componentProps} />;
  }
}
